import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Users, Mail, Share2, QrCode, Copy, Search, Check, X, Send,
  Plus, Trash2, MoreHorizontal, Download, MessageSquare, Link2, Sparkles,
} from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api, API } from "../../lib/api";
import { fmtDatePT, fmtTimePT, fmtWeekdayPT } from "../../lib/time";
import { useUrlPatch, useUrlState } from "../../lib/useUrlState";
import { resolveImageUrl } from "../../lib/media";
import ReportTab from "./ReportTab";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "guests", label: "Guests" },
  { key: "registration", label: "Registration" },
  { key: "blasts", label: "Broadcasts" },
  { key: "feedback", label: "Feedback" },
  { key: "report", label: "Report" },
  { key: "insights", label: "Insights" },
  { key: "more", label: "More" },
];

export default function AdminEventManage() {
  const { id } = useParams();
  const nav = useNavigate();
  // The open tab is part of the address, so /admin/events/:id/manage?tab=guests
  // is a real link you can send someone (see lib/useUrlState). push: Back returns
  // to the tab you came from rather than leaving the page.
  const [tab, setTab] = useUrlState("tab", "overview", { push: true });
  const [ev, setEv] = useState(null);

  const load = () => api.get(`/events/${id}`).then((r) => setEv(r.data)).catch(() => setEv(null));
  useEffect(() => { load(); }, [id]);

  if (!ev) return (
    <AdminShell><div className="skeleton h-96" /></AdminShell>
  );

  const publicUrl = `${window.location.origin}/events/${ev.short_slug || ev.id}`;

  return (
    <AdminShell testId="admin-event-manage">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/admin/events" className="hover:text-foreground">Events</Link>
          <span>/</span>
          <span className="text-foreground truncate max-w-[400px]">{ev.title}</span>
        </div>
        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <h1 className="text-3xl md:text-4xl font-heading font-medium tracking-tight">{ev.title}</h1>
          <div className="flex gap-2">
            <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="event-page-btn">
              Event Page <ExternalLink size={13} />
            </a>
            <Link to={`/admin/events/${ev.id}/edit`} className="btn-ghost text-sm" data-testid="edit-event-btn">Edit</Link>
          </div>
        </div>

        {/* Tabs — slotted events (1:1 sessions) get a Slots tab right after Guests. */}
        <div className="mt-8 flex gap-1 border-b border-border overflow-x-auto">
          {(ev.format === "slots"
            ? [...TABS.slice(0, 2), { key: "slots", label: "Slots" }, ...TABS.slice(2)]
            : TABS).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm border-b-2 transition -mb-px whitespace-nowrap ${
                tab === t.key ? "border-foreground text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`manage-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && <OverviewTab ev={ev} publicUrl={publicUrl} onChange={load} setTab={setTab} />}
          {tab === "guests" && <GuestsTab ev={ev} onChange={load} />}
          {tab === "slots" && <SlotsTab ev={ev} />}
          {tab === "registration" && <RegistrationTab ev={ev} onChange={load} />}
          {tab === "blasts" && <BlastsTab ev={ev} />}
          {tab === "feedback" && <FeedbackTab ev={ev} onChange={load} />}
          {tab === "report" && <ReportTab ev={ev} />}
          {tab === "insights" && <InsightsTab ev={ev} />}
          {tab === "more" && <MoreTab ev={ev} onChange={load} nav={nav} />}
        </div>
    </AdminShell>
  );
}

/* ---------------- OVERVIEW ---------------- */
function OverviewTab({ ev, publicUrl, onChange, setTab }) {
  const copy = () => { navigator.clipboard.writeText(publicUrl); toast.success("Public link copied"); };
  const [shareLink, setShareLink] = useState(null);
  const genShare = async () => {
    try {
      const r = await api.post(`/events/${ev.id}/share-token`);
      const link = `${window.location.origin}/e/${r.data.token}`;
      setShareLink(link);
      await navigator.clipboard.writeText(link);
      toast.success("Locked link copied to clipboard");
    } catch { toast.error("Failed to generate link"); }
  };
  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="overview-tab">
      <div className="space-y-6">
        <div className="card-hair p-6">
          <Overline>Quick actions</Overline>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <button className="card-hair p-4 hover-lift text-left" data-testid="quick-invite">
              <Users size={16} style={{ color: "hsl(var(--vd-blue))" }} />
              <div className="mt-3 font-medium text-sm">Invite Guests</div>
              <div className="text-xs text-muted-foreground mt-1">Add attendees manually</div>
            </button>
            <button onClick={() => setTab && setTab("blasts")} className="card-hair p-4 hover-lift text-left" data-testid="quick-blast">
              <Mail size={16} style={{ color: "hsl(var(--vd-orange))" }} />
              <div className="mt-3 font-medium text-sm">Send a Broadcast</div>
              <div className="text-xs text-muted-foreground mt-1">Email or in-app broadcast</div>
            </button>
            <button onClick={() => setTab && setTab("feedback")} className="card-hair p-4 hover-lift text-left" data-testid="quick-feedback">
              <MessageSquare size={16} style={{ color: "hsl(var(--vd-green))" }} />
              <div className="mt-3 font-medium text-sm">Send Feedback</div>
              <div className="text-xs text-muted-foreground mt-1">Manually send NPS survey</div>
            </button>
            <button onClick={copy} className="card-hair p-4 hover-lift text-left" data-testid="quick-share">
              <Share2 size={16} style={{ color: "hsl(var(--vd-purple))" }} />
              <div className="mt-3 font-medium text-sm">Share Event</div>
              <div className="text-xs text-muted-foreground mt-1">Copy the public link</div>
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <button onClick={genShare} className="btn-ghost text-sm" data-testid="private-share-btn">
              <Share2 size={12} /> Generate locked share link
            </button>
            {shareLink && (
              <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-white/60 border border-white/60" data-testid="private-share-panel">
                <input readOnly value={shareLink} className="input-field text-xs font-mono flex-1" onFocus={(e) => e.target.select()} data-testid="private-share-link" />
                <button onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Copied"); }} className="btn-ghost text-xs" data-testid="private-share-copy"><Copy size={11} /></button>
              </div>
            )}
            <div className="text-[11px] text-muted-foreground mt-1.5">Opens the event in a chrome-less standalone page — no nav, no other events visible.</div>
          </div>
        </div>

        <ShareLinksCard ev={ev} publicUrl={publicUrl} />

        <div className="card-hair p-6">
          <Overline>When & where</Overline>
          <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-medium">{fmtWeekdayPT(ev.start_at)}, {fmtDatePT(ev.start_at, { month: "long", day: "numeric" })} · {fmtTimePT(ev.start_at)} PT</div>
              <div className="text-sm text-muted-foreground mt-0.5">{ev.campus?.name || ev.custom_venue || (ev.virtual_link ? "Virtual" : "TBD")}</div>
            </div>
            <a href={`/admin/events/${ev.id}/checkin`} className="btn-primary text-sm" data-testid="checkin-btn">
              <QrCode size={13} /> Check In Guests
            </a>
          </div>
        </div>

        <div className="card-hair p-6">
          <Overline>Hosts</Overline>
          <div className="mt-4 flex flex-wrap gap-2">
            {(ev.hosts || []).map((h, i) => (
              <div key={h.member_id || h.email || `host-${i}`} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-border">
                <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ background: h.role === "creator" ? "hsl(var(--vd-blue))" : "hsl(var(--vd-purple))", color: h.role === "creator" ? "#fff" : "hsl(var(--vd-purple-ink))" }}>
                  {h.name?.[0]}
                </div>
                <span className="text-sm">{h.name}</span>
                <span className={`chip ${h.role === "creator" ? "chip-blue" : h.role === "checkin" ? "chip-green" : "chip-purple"} text-[10px]`}>{h.role}</span>
              </div>
            ))}
            <button className="btn-ghost text-sm h-9" data-testid="add-host-btn"><Plus size={12} />Add Host</button>
          </div>
        </div>
      </div>

      {/* Right: preview card */}
      <div className="space-y-4">
        <div className="card-hair overflow-hidden">
          {/* Luma covers are square (1200x1200); native ones are authored for
              the wide box. One fixed ratio crops whichever does not fit. */}
          <div className={`overflow-hidden ${ev.source === "luma" ? "aspect-square" : "aspect-[16/9]"}`}>
            <img src={resolveImageUrl(ev.cover_image)} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="p-5">
            <div className="font-heading text-lg leading-snug">{ev.title}</div>
            <div className="mt-2 text-xs text-muted-foreground font-mono uppercase">
              {fmtDatePT(ev.start_at, { weekday: "short", month: "short", day: "numeric" })} · {fmtTimePT(ev.start_at)} PT
            </div>
          </div>
        </div>
        <div className="card-hair p-4">
          <div className="text-xs text-muted-foreground">Public link</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="text-xs font-mono truncate flex-1 px-2 py-1.5 rounded-lg bg-secondary">{publicUrl}</code>
            <button onClick={copy} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" data-testid="overview-copy-btn"><Copy size={13} /></button>
          </div>
        </div>
        <div className="card-hair p-4">
          <Overline>Visibility & discovery</Overline>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className={`chip ${ev.visibility === "public" ? "chip-orange" : ev.visibility === "members_only" ? "chip-purple" : "chip-magenta"}`}>{ev.visibility}</span>
            <span className="chip chip-cream-2">{ev.privacy}</span>
            {ev.require_approval && <span className="chip chip-cream">Approval</span>}
            {ev.waitlist_enabled && <span className="chip chip-blue">Waitlist</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- SHARE LINKS (UTM presets) ---------------- */
const UTM_TEMPLATES = [
  { key: "linkedin",  label: "LinkedIn",  utm_source: "linkedin",  utm_medium: "social",    icon: "in" },
  { key: "twitter",   label: "X / Twitter",   utm_source: "twitter",   utm_medium: "social",    icon: "x" },
  { key: "instagram", label: "Instagram", utm_source: "instagram", utm_medium: "social",    icon: "ig" },
  { key: "facebook",  label: "Facebook",  utm_source: "facebook",  utm_medium: "social",    icon: "fb" },
  { key: "slack",     label: "Slack",     utm_source: "slack",     utm_medium: "chat",      icon: "sl" },
  { key: "email",     label: "Email",     utm_source: "email",     utm_medium: "email",     icon: "@" },
  { key: "newsletter", label: "Newsletter", utm_source: "newsletter", utm_medium: "email", icon: "nl" },
  { key: "whatsapp",  label: "WhatsApp",  utm_source: "whatsapp",  utm_medium: "chat",      icon: "wa" },
];

function ShareLinksCard({ ev, publicUrl }) {
  const [presets, setPresets] = useState([]);
  const [tpl, setTpl] = useState("linkedin");
  const [campaign, setCampaign] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${ev.id}/utm-presets`).then((r) => setPresets(r.data)).catch(() => setPresets([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ev.id]);

  const activeTpl = UTM_TEMPLATES.find((t) => t.key === tpl) || UTM_TEMPLATES[0];

  const create = async () => {
    setBusy(true);
    try {
      const body = {
        label: customLabel.trim() || activeTpl.label,
        utm_source: activeTpl.utm_source,
        utm_medium: activeTpl.utm_medium,
        utm_campaign: campaign.trim().toLowerCase().replace(/\s+/g, "-") || null,
      };
      await api.post(`/events/${ev.id}/utm-presets`, body);
      toast.success(`${body.label} link ready`);
      setCampaign("");
      setCustomLabel("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    } finally { setBusy(false); }
  };

  const copyUrl = (url, label) => {
    navigator.clipboard.writeText(url);
    toast.success(`${label} link copied`);
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this share link? (Existing links that people already have will still work — this only removes it from your history.)")) return;
    try { await api.delete(`/events/${ev.id}/utm-presets/${id}`); load(); }
    catch { toast.error("Failed to remove"); }
  };

  const defaultCampaign = (ev.short_slug || ev.id.slice(-8)).toLowerCase();

  return (
    <div className="card-hair p-6" data-testid="share-links-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Overline>Share links</Overline>
          <div className="text-xs text-muted-foreground mt-1">
            Generate tagged URLs to track which channel brought each registration. Data flows into <span className="font-mono">Community → Traffic sources</span>.
          </div>
        </div>
      </div>

      {/* Builder */}
      <div className="mt-5 rounded-2xl p-4 border border-border bg-white/50">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Quick generate</div>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="utm-template-picker">
          {UTM_TEMPLATES.map((t) => (
            <button
              key={t.key}
              onClick={() => setTpl(t.key)}
              className={`chip text-xs ${tpl === t.key ? "chip-green" : "chip-cream-2"}`}
              data-testid={`utm-tpl-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-2">
          <input
            className="input-field text-sm"
            placeholder={`Campaign (e.g. ${defaultCampaign})`}
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            data-testid="utm-campaign-input"
          />
          <input
            className="input-field text-sm"
            placeholder={`Label (default: ${activeTpl.label})`}
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            data-testid="utm-label-input"
          />
          <button onClick={create} disabled={busy} className="btn-primary text-sm" data-testid="utm-create-btn">
            <Sparkles size={12} /> {busy ? "Generating…" : "Generate & save"}
          </button>
        </div>
        <div className="mt-3 text-[11px] text-muted-foreground font-mono">
          Preview: <span className="text-foreground">{publicUrl}?utm_source={activeTpl.utm_source}&amp;utm_medium={activeTpl.utm_medium}{campaign ? `&utm_campaign=${campaign.trim().toLowerCase().replace(/\s+/g, "-")}` : ""}</span>
        </div>
      </div>

      {/* Saved list */}
      <div className="mt-5" data-testid="utm-preset-list">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Saved links · {presets.length}</div>
        {presets.length === 0 ? (
          <div className="mt-3 text-sm text-muted-foreground">No saved links yet. Generate one above and share it on that channel.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {presets.map((p) => (
              <div key={p.id} className="rounded-xl p-3 border border-white/60 bg-white/40" data-testid={`utm-preset-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm">{p.label}</div>
                      <span className="chip chip-cream-2 text-[10px]">{p.utm_source}</span>
                      {p.utm_medium && <span className="chip chip-cream text-[10px]">{p.utm_medium}</span>}
                      {p.utm_campaign && <span className="chip chip-green text-[10px]">{p.utm_campaign}</span>}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <code className="text-[11px] font-mono truncate flex-1 px-2 py-1.5 rounded-lg bg-secondary" title={p.url}>{p.url}</code>
                      <button onClick={() => copyUrl(p.url, p.label)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" data-testid={`utm-copy-${p.id}`} title="Copy URL"><Copy size={13} /></button>
                      <a href={p.url} target="_blank" rel="noreferrer" className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" title="Open link"><ExternalLink size={13} /></a>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-xs font-mono text-muted-foreground">
                      <span className="text-foreground font-medium">{p.registrations}</span> regs · <span className="text-foreground font-medium">{p.checkins}</span> checkins
                    </div>
                    <button onClick={() => remove(p.id)} className="btn-ghost text-xs" data-testid={`utm-remove-${p.id}`}><Trash2 size={11} />Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- GUESTS ---------------- */
const STATUS_META = {
  going: { label: "Going", chip: "chip-green" },
  pending: { label: "Pending", chip: "chip-cream" },
  invited: { label: "Invited", chip: "chip-blue" },
  waitlist: { label: "Waitlist", chip: "chip-orange" },
  declined: { label: "Declined", chip: "chip-coral" },
  checked_in: { label: "Checked in", chip: "chip-purple" },
};

function GuestsTab({ ev, onChange }) {
  const [regs, setRegs] = useState([]);
  // Filter and search ride the URL alongside ?tab=guests, so "the pending guests
  // for this event" is a link. The search BOX stays local state and drives the
  // filtering immediately (this list is filtered client-side); the URL copy is
  // debounced behind it, purely so the view is shareable.
  const [filter, setFilter] = useUrlState("status", "all", { push: true });
  const [q] = useUrlState("q", "");
  const patch = useUrlPatch();
  const [qInput, setQInput] = useState(q);
  const [showAdd, setShowAdd] = useState(false);
  const [manual, setManual] = useState({ name: "", email: "", phone: "" });

  const load = () => api.get(`/events/${ev.id}/registrations`).then((r) => setRegs(r.data));
  useEffect(() => { load(); }, [ev.id]);

  // Mirror the box into the URL once typing settles. Replaces rather than
  // pushes: a history entry per keystroke would make Back useless.
  useEffect(() => {
    if (qInput === q) return undefined;
    const t = setTimeout(() => patch({ q: qInput }), 300);
    return () => clearTimeout(t);
  }, [qInput, q, patch]);

  // Back/forward (or a pasted link) moves the URL under us; keep the box in step.
  // No loop: the debounce above skips when the two already agree.
  useEffect(() => { setQInput(q); }, [q]);

  const filtered = regs.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (qInput && !`${r.name} ${r.email}`.toLowerCase().includes(qInput.toLowerCase())) return false;
    return true;
  });

  const counts = Object.keys(STATUS_META).reduce((acc, k) => {
    acc[k] = regs.filter((r) => r.status === k).length;
    return acc;
  }, { all: regs.length });

  // The API caps the roster. If we got exactly the cap the list is almost
  // certainly cut, and every count above is a count of the SURVIVING slice —
  // which is how "All · 2000 / Going · 48" sat next to Foundry's true 4,983
  // RSVPs and 127 approved without anything looking wrong.
  const REGISTRATIONS_CAP = 25000;
  const truncated = regs.length >= REGISTRATIONS_CAP;

  const decide = async (reg, status) => {
    try {
      await api.post(`/events/${ev.id}/registrations/${reg.id}/decision`, { status });
      toast.success(`${reg.name} → ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  // One-click front-desk check-in, straight from the guest row.
  const checkin = async (reg) => {
    try {
      await api.post(`/events/${ev.id}/registrations/${reg.id}/checkin`);
      toast.success(`${reg.name} checked in`);
      load();
    } catch { toast.error("Check-in failed"); }
  };
  const undoCheckin = async (reg) => {
    try {
      await api.post(`/events/${ev.id}/registrations/${reg.id}/undo-checkin`);
      toast.success(`Check-in undone for ${reg.name}`);
      load();
    } catch { toast.error("Undo failed"); }
  };

  const addManual = async () => {
    if (!manual.name || !manual.email) return toast.error("Name & email required");
    try {
      await api.post(`/events/${ev.id}/registrations/manual`, { ...manual });
      toast.success("Added");
      setShowAdd(false); setManual({ name: "", email: "", phone: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const exportUrl = `${API}/events/${ev.id}/registrations/export`;

  return (
    <div className="space-y-6" data-testid="guests-tab">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setFilter("all")} data-testid="filter-all" className={`chip ${filter === "all" ? "chip-blue" : ""}`}>All · {counts.all}</button>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button
            key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`chip ${filter === k ? m.chip : ""}`}
          >{m.label} · {counts[k] || 0}</button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="input-field pl-8 py-2 text-sm w-56" placeholder="Search guests…" value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="guests-search" />
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-ghost text-sm" data-testid="add-guest-btn"><Plus size={12} />Add</button>
        <a href={exportUrl} className="btn-ghost text-sm" data-testid="export-csv-btn"><Download size={12} />Export</a>
      </div>

      <div className="card-hair overflow-hidden">
        <div className="grid grid-cols-[1.5fr_1fr_0.8fr_1fr_auto] px-4 py-3 text-xs overline text-muted-foreground border-b border-border">
          <div>Guest</div><div>Ticket</div><div>Status</div><div>QR</div><div></div>
        </div>
        {truncated && (
          <div className="px-4 py-3 text-xs border-b border-border"
               style={{ background: "hsl(var(--vd-coral) / 0.1)", color: "hsl(var(--vd-coral))" }}
               data-testid="guests-truncated">
            Showing the most recent {REGISTRATIONS_CAP.toLocaleString()} registrations — this
            event has more, so the counts above describe only what is loaded. Export for the
            full list.
          </div>
        )}
        {filtered.length === 0 && <div className="p-8 text-sm text-muted-foreground text-center">No guests match.</div>}
        {filtered.map((r) => (
          <div key={r.id} className="grid grid-cols-[1.5fr_1fr_0.8fr_1fr_auto] px-4 py-3 items-center text-sm border-b border-border last:border-b-0" data-testid={`guest-${r.id}`}>
            <div>
              <div className="font-medium">{r.name}</div>
              <div className="text-xs text-muted-foreground">{r.email}</div>
            </div>
            <div className="text-muted-foreground">
              {(ev.ticket_types || []).find((t) => t.id === r.ticket_type_id)?.name || "General"}
              {r.is_member && <span className="chip chip-purple ml-2 text-[10px]">Member</span>}
            </div>
            <div>
              <span className={`chip text-[10px] ${STATUS_META[r.status]?.chip || ""}`}>{STATUS_META[r.status]?.label || r.status}</span>
            </div>
            <div className="font-mono text-[10px] text-muted-foreground truncate">{r.qr_code}</div>
            <div className="flex items-center gap-1">
              {r.status === "pending" && (
                <>
                  <button onClick={() => decide(r, "going")} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" title="Approve" data-testid={`approve-${r.id}`}>
                    <Check size={14} style={{ color: "hsl(var(--vd-green))" }} />
                  </button>
                  <button onClick={() => decide(r, "declined")} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" title="Decline" data-testid={`decline-${r.id}`}>
                    <X size={14} style={{ color: "hsl(var(--vd-coral))" }} />
                  </button>
                </>
              )}
              {r.status === "waitlist" && (
                <button onClick={() => decide(r, "going")} className="h-8 px-2 flex items-center gap-1 rounded-lg hover:bg-secondary text-xs" data-testid={`promote-${r.id}`}>
                  <Check size={12} /> Promote
                </button>
              )}
              {r.status === "going" && (
                <button onClick={() => checkin(r)} className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg text-xs font-medium text-white" style={{ background: "hsl(var(--vd-green))" }} title="Check in" data-testid={`checkin-${r.id}`}>
                  <Check size={12} /> Check in
                </button>
              )}
              {r.status === "checked_in" && (
                <button onClick={() => undoCheckin(r)} className="h-8 px-2 flex items-center gap-1 rounded-lg hover:bg-secondary text-xs text-muted-foreground" title="Undo check-in" data-testid={`undo-checkin-${r.id}`}>
                  Undo
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="card-hair p-6 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()} data-testid="manual-add-modal">
            <div className="font-heading text-lg">Add guest manually</div>
            <input className="input-field" placeholder="Name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="manual-name" />
            <input className="input-field" placeholder="Email" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} data-testid="manual-email" />
            <input className="input-field" placeholder="Phone (optional)" value={manual.phone} onChange={(e) => setManual({ ...manual, phone: e.target.value })} data-testid="manual-phone" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAdd(false)} className="btn-ghost">Cancel</button>
              <button onClick={addManual} className="btn-primary" data-testid="manual-add-submit">Add guest</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- SLOTS (1:1 sessions) ---------------- */
function SlotsTab({ ev }) {
  const [slots, setSlots] = useState(null);
  const [newTime, setNewTime] = useState("");

  const load = () => api.get(`/admin/events/${ev.id}/slots`).then((r) => setSlots(r.data)).catch(() => setSlots([]));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ev.id]);

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.error || e.response?.data?.detail || "Failed"); }
  };
  const decide = (s, status) => act(
    () => api.post(`/events/${ev.id}/registrations/${s.registration_id}/decision`, { status }),
    status === "going" ? "Approved — slot booked" : "Declined — slot released");
  const checkin = (s) => act(() => api.post(`/events/${ev.id}/registrations/${s.registration_id}/checkin`), "Checked in");
  const undoCheckin = (s) => act(() => api.post(`/events/${ev.id}/registrations/${s.registration_id}/undo-checkin`), "Check-in undone");
  const removeSlot = (s) => act(() => api.delete(`/admin/events/${ev.id}/slots/${s.id}`), "Open slot removed");
  const addSlot = () => {
    if (!newTime) return toast.error("Pick a start time");
    act(() => api.post(`/admin/events/${ev.id}/slots`, { start_at: new Date(newTime).toISOString() }), "Slot added")
      .then?.(() => {});
    setNewTime("");
  };

  if (slots === null) return <div className="skeleton h-40" />;
  const booked = slots.filter((s) => s.status === "booked").length;
  const pending = slots.filter((s) => s.reg_status === "pending").length;

  return (
    <div className="space-y-5" data-testid="slots-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="chip chip-green text-[11px]">{booked} of {slots.length} booked</span>
        {pending > 0 && <span className="chip chip-cream-2 text-[11px]">{pending} pending approval</span>}
        <div className="flex-1" />
        <input type="datetime-local" className="input-field py-2 text-sm" value={newTime} onChange={(e) => setNewTime(e.target.value)} data-testid="slot-add-time" />
        <button onClick={addSlot} className="btn-ghost text-sm" data-testid="slot-add-btn"><Plus size={12} /> Add open slot</button>
      </div>

      <div className="card-hair overflow-hidden">
        <div className="grid grid-cols-[1fr_1.6fr_1fr_auto] px-4 py-3 text-xs overline text-muted-foreground border-b border-border">
          <div>Time</div><div>Booked by</div><div>Status</div><div></div>
        </div>
        {slots.map((s) => (
          <div key={s.id} className="grid grid-cols-[1fr_1.6fr_1fr_auto] px-4 py-3 items-center text-sm border-b border-border last:border-b-0" data-testid={`slot-${s.id}`}>
            <div className="font-mono text-xs">{fmtTimePT(s.start_at)}–{fmtTimePT(s.end_at)} PT</div>
            <div>
              {s.registration_id ? (
                <>
                  <div className="font-medium">{s.reg_name}</div>
                  <div className="text-xs text-muted-foreground">{s.reg_email}</div>
                </>
              ) : <span className="text-muted-foreground text-xs">—</span>}
            </div>
            <div>
              {s.reg_status === "pending" ? <span className="chip chip-cream-2 text-[10px]">Pending approval</span>
                : s.checked_in ? <span className="chip chip-green text-[10px]">Checked in</span>
                : s.status === "booked" ? <span className="chip chip-blue text-[10px]">Booked</span>
                : <span className="chip text-[10px]">Open</span>}
            </div>
            <div className="flex items-center gap-1">
              {s.reg_status === "pending" && (
                <>
                  <button onClick={() => decide(s, "going")} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" title="Approve" data-testid={`slot-approve-${s.id}`}>
                    <Check size={14} style={{ color: "hsl(var(--vd-green))" }} />
                  </button>
                  <button onClick={() => decide(s, "declined")} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" title="Decline" data-testid={`slot-decline-${s.id}`}>
                    <X size={14} style={{ color: "hsl(var(--vd-coral))" }} />
                  </button>
                </>
              )}
              {s.reg_status === "going" && (
                <button onClick={() => checkin(s)} className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg text-xs font-medium text-white" style={{ background: "hsl(var(--vd-green))" }} data-testid={`slot-checkin-${s.id}`}>
                  <Check size={12} /> Check in
                </button>
              )}
              {s.reg_status === "checked_in" && (
                <button onClick={() => undoCheckin(s)} className="h-8 px-2 rounded-lg hover:bg-secondary text-xs text-muted-foreground" data-testid={`slot-undo-${s.id}`}>Undo</button>
              )}
              {!s.registration_id && s.status === "available" && (
                <button onClick={() => removeSlot(s)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" title="Remove open slot" data-testid={`slot-remove-${s.id}`}>
                  <X size={14} className="text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        ))}
        {slots.length === 0 && <div className="p-8 text-sm text-muted-foreground text-center">No slots yet — add one above.</div>}
      </div>
    </div>
  );
}

/* ---------------- REGISTRATION ---------------- */
function RegistrationTab({ ev, onChange }) {
  const [state, setState] = useState({
    registration_open: ev.registration_open,
    waitlist_enabled: ev.waitlist_enabled,
    group_registration: ev.group_registration,
    require_approval: ev.require_approval,
    ticket_types: ev.ticket_types || [],
    custom_questions: ev.custom_questions || [],
    email_templates: ev.email_templates || { pending: "", going: "", declined: "", waitlist: "" },
  });

  const save = async () => {
    try {
      await api.patch(`/events/${ev.id}`, {
        title: ev.title, description: ev.description, cover_image: ev.cover_image,
        start_at: ev.start_at, end_at: ev.end_at, timezone: ev.timezone,
        campus_id: ev.campus_id, custom_venue: ev.custom_venue, virtual_link: ev.virtual_link,
        visibility: ev.visibility, privacy: ev.privacy,
        capacity: ev.capacity, ticket_price: ev.ticket_price,
        hosts: ev.hosts,
        ...state,
      });
      toast.success("Saved");
      onChange();
    } catch { toast.error("Failed to save"); }
  };

  const addTicket = () => setState({ ...state, ticket_types: [...state.ticket_types, { _key: crypto.randomUUID(), name: "New ticket", price: 0, require_approval: false }] });
  const addQuestion = () => setState({ ...state, custom_questions: [...state.custom_questions, { _key: crypto.randomUUID(), label: "New question", type: "text", required: false, options: [] }] });

  return (
    <div className="space-y-6" data-testid="registration-tab">
      <div className="grid md:grid-cols-3 gap-4">
        <Toggle
          card="card-hair"
          title="Registration"
          value={state.registration_open}
          setValue={(v) => setState({ ...state, registration_open: v })}
          on="Open" off="Closed"
          testId="toggle-registration"
        />
        <Toggle
          title="Event capacity — Waitlist"
          value={state.waitlist_enabled}
          setValue={(v) => setState({ ...state, waitlist_enabled: v })}
          on="On" off="Off"
          testId="toggle-waitlist"
        />
        <Toggle
          title="Group Registration"
          value={state.group_registration}
          setValue={(v) => setState({ ...state, group_registration: v })}
          on="On" off="Off"
          testId="toggle-group"
        />
      </div>

      {/* Ticket types */}
      <div className="card-hair p-6">
        <div className="flex items-center justify-between">
          <Overline>Ticket types</Overline>
          <button onClick={addTicket} className="btn-ghost text-sm" data-testid="add-ticket-btn"><Plus size={12} />New ticket type</button>
        </div>
        <div className="mt-4 space-y-3">
          {state.ticket_types.length === 0 && <div className="text-sm text-muted-foreground">No ticket types yet. Add one to allow tiered registration.</div>}
          {state.ticket_types.map((t, i) => (
            <div key={t.id || t._key || `t-${i}`} className="grid grid-cols-[1.5fr_100px_auto_auto] gap-3 items-center p-4 rounded-2xl border border-border" data-testid={`ticket-${i}`}>
              <input className="input-field" placeholder="Ticket name" value={t.name} onChange={(e) => {
                const arr = [...state.ticket_types]; arr[i] = { ...t, name: e.target.value }; setState({ ...state, ticket_types: arr });
              }} />
              <input type="number" className="input-field" value={t.price} onChange={(e) => {
                const arr = [...state.ticket_types]; arr[i] = { ...t, price: parseFloat(e.target.value || 0) }; setState({ ...state, ticket_types: arr });
              }} />
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={t.require_approval} onChange={(e) => {
                  const arr = [...state.ticket_types]; arr[i] = { ...t, require_approval: e.target.checked }; setState({ ...state, ticket_types: arr });
                }} className="accent-primary" />
                Approval
              </label>
              <button onClick={() => setState({ ...state, ticket_types: state.ticket_types.filter((_, j) => j !== i) })} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" style={{ color: "hsl(var(--vd-coral))" }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Custom questions */}
      <div className="card-hair p-6">
        <div className="flex items-center justify-between">
          <Overline>Registration questions</Overline>
          <button onClick={addQuestion} className="btn-ghost text-sm" data-testid="add-question-btn"><Plus size={12} />Add question</button>
        </div>
        <div className="mt-4 space-y-2">
          <div className="p-3 rounded-xl bg-secondary text-sm text-muted-foreground">Name and Email are always collected.</div>
          {state.custom_questions.map((q, i) => (
            <div key={q.id || q._key || `q-${i}`} className="grid grid-cols-[1.5fr_auto_auto_auto] gap-3 items-center p-3 rounded-2xl border border-border" data-testid={`question-${i}`}>
              <input className="input-field" value={q.label} onChange={(e) => {
                const arr = [...state.custom_questions]; arr[i] = { ...q, label: e.target.value }; setState({ ...state, custom_questions: arr });
              }} />
              <select className="input-field w-36" value={q.type} onChange={(e) => {
                const arr = [...state.custom_questions]; arr[i] = { ...q, type: e.target.value }; setState({ ...state, custom_questions: arr });
              }}>
                <option value="text">Text</option>
                <option value="select">Select</option>
                <option value="checkbox">Checkbox</option>
              </select>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={q.required} onChange={(e) => {
                  const arr = [...state.custom_questions]; arr[i] = { ...q, required: e.target.checked }; setState({ ...state, custom_questions: arr });
                }} className="accent-primary" />
                Required
              </label>
              <button onClick={() => setState({ ...state, custom_questions: state.custom_questions.filter((_, j) => j !== i) })} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary" style={{ color: "hsl(var(--vd-coral))" }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Email templates */}
      <div className="card-hair p-6">
        <Overline>Registration emails</Overline>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {["pending", "going", "declined", "waitlist"].map((k) => (
            <div key={k}>
              <div className="text-xs text-muted-foreground mb-1 capitalize">{k} template</div>
              <textarea rows={3} className="input-field resize-none text-sm" placeholder={`Message shown to ${k} registrants…`}
                value={state.email_templates[k] || ""}
                onChange={(e) => setState({ ...state, email_templates: { ...state.email_templates, [k]: e.target.value } })}
                data-testid={`template-${k}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} className="btn-primary" data-testid="registration-save-btn">Save changes</button>
      </div>
    </div>
  );
}

function Toggle({ title, value, setValue, on = "On", off = "Off", testId }) {
  return (
    <div className="card-hair p-5">
      <div className="text-xs text-muted-foreground uppercase tracking-widest">{title}</div>
      <div className="mt-3 flex items-center justify-between">
        <div className="font-heading text-lg">{value ? on : off}</div>
        <button
          onClick={() => setValue(!value)}
          className="relative h-6 w-11 rounded-full transition"
          style={{ background: value ? "hsl(var(--vd-blue))" : "hsl(0 0% 88%)" }}
          data-testid={testId}
          type="button"
        >
          <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" style={{ left: value ? "22px" : "2px" }} />
        </button>
      </div>
    </div>
  );
}

/* ---------------- BLASTS ---------------- */
function BlastsTab({ ev }) {
  const [blasts, setBlasts] = useState([]);
  const [form, setForm] = useState({ subject: "", body: "", segment: "all", channel: "email" });
  const load = () => api.get(`/events/${ev.id}/blasts`).then((r) => setBlasts(r.data));
  useEffect(() => { load(); }, [ev.id]);
  const send = async () => {
    if (!form.subject || !form.body) return toast.error("Subject and message required");
    try {
      await api.post(`/events/${ev.id}/blasts`, form);
      toast.success("Broadcast sent");
      setForm({ subject: "", body: "", segment: "all", channel: "email" });
      load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6" data-testid="blasts-tab">
      <div className="card-hair p-6">
        <Overline>Compose</Overline>
        <div className="mt-4 space-y-3">
          <input className="input-field" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="blast-subject" />
          <textarea rows={6} className="input-field resize-none" placeholder="Your message…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="blast-body" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-field" value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} data-testid="blast-segment">
              <option value="all">All registrants</option>
              <option value="going">Going</option>
              <option value="pending">Pending approval</option>
              <option value="checked_in">Checked in</option>
              <option value="waitlist">Waitlist</option>
            </select>
            <select className="input-field" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="blast-channel">
              <option value="email">Email</option>
              <option value="in_app">In-app</option>
              <option value="both">Both</option>
            </select>
          </div>
          <button onClick={send} className="btn-primary w-full" data-testid="blast-send-btn"><Send size={13} />Send broadcast</button>
        </div>
      </div>
      <div>
        <Overline>Send history</Overline>
        <div className="mt-4 space-y-2">
          {blasts.length === 0 && <div className="text-sm text-muted-foreground">No broadcasts sent yet.</div>}
          {blasts.map((b) => (
            <div key={b.id} className="card-hair p-4" data-testid={`blast-${b.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{b.subject}</div>
                <span className="chip chip-cream-2 text-[10px]">{b.channel} · {b.segment}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{b.body}</div>
              <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                {b.recipient_count} recipients · {new Date(b.sent_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- FEEDBACK ---------------- */
function FeedbackTab({ ev }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [audience, setAudience] = useState(ev.feedback_audience || "checked_in");
  const [enabled, setEnabled] = useState(ev.feedback_automation_enabled !== false);
  const [delay, setDelay] = useState(ev.feedback_delay_minutes ?? 120);

  const load = () => api.get(`/admin/events/${ev.id}/feedback`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ev.id]);

  const sendNow = async () => {
    if (!window.confirm(`Send feedback to ${audience === "checked_in" ? "checked-in attendees" : "all registered"}?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/events/${ev.id}/feedback/send`, { audience });
      toast.success(`${r.data.sent} feedback email${r.data.sent === 1 ? "" : "s"} sent (skipped ${r.data.skipped} already-sent)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resendReminders = async () => {
    if (!window.confirm("Send a reminder to everyone who hasn't responded yet?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/events/${ev.id}/feedback/send`, { resend_to_nonresponders: true });
      toast.success(`Reminder sent to ${r.data.resent} non-responders`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveSettings = async (patch) => {
    try {
      await api.patch(`/admin/events/${ev.id}/feedback`, patch);
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="skeleton h-64" />;
  const npsColour = (b) => b === "promoter" ? "hsl(var(--vd-green))" : b === "detractor" ? "hsl(var(--vd-coral, 4 84% 60%))" : "hsl(var(--muted-foreground))";

  return (
    <div className="space-y-6">
      {/* Hero send-now CTA */}
      <div className="glass rounded-2xl p-6 md:p-7" style={{ borderColor: "hsl(var(--vd-green) / 0.35)" }}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(var(--vd-green) / 0.12)", color: "hsl(var(--vd-green))" }}>
            <MessageSquare size={20} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-heading text-lg md:text-xl font-medium tracking-tight">Send NPS feedback survey</div>
            <div className="text-sm text-muted-foreground mt-1">
              Emails a tokenized 0–10 NPS survey to attendees. {data.sent > 0 ? `${data.sent} already sent · ${data.responded} responded (${data.response_rate}%).` : "None sent yet."}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
            <select className="input-field text-sm" value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="feedback-audience">
              <option value="checked_in">Checked-in attendees</option>
              <option value="all_registered">All registered</option>
            </select>
            <button
              onClick={sendNow}
              disabled={busy}
              className="btn-primary text-sm font-medium px-6 py-3 rounded-xl inline-flex items-center gap-2 justify-center shadow-md hover:shadow-lg transition disabled:opacity-50"
              data-testid="feedback-send-now"
            >
              <Send size={15} />
              {busy ? "Sending…" : "Send now"}
            </button>
          </div>
        </div>
        {data.responded < data.sent && (
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground">
              {data.sent - data.responded} attendee{data.sent - data.responded === 1 ? "" : "s"} hasn't responded yet.
            </div>
            <button onClick={resendReminders} disabled={busy} className="btn-ghost text-sm" data-testid="feedback-remind">Remind non-responders</button>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid md:grid-cols-4 gap-3">
        <Stat label="Sent" value={data.sent} />
        <Stat label="Responded" value={data.responded} />
        <Stat label="Response rate" value={`${data.response_rate}%`} />
        <Stat label="NPS" value={data.nps_score ?? "—"} />
      </div>

      {/* Automation settings */}
      <div className="glass rounded-2xl p-6 space-y-4">
        <Overline>Automation settings</Overline>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); saveSettings({ enabled: e.target.checked }); }} data-testid="feedback-toggle-auto" />
            Automatic post-event send
          </label>
          <label className="flex items-center gap-2">
            Delay after event ends
            <select className="input-field text-sm py-1" value={delay} onChange={(e) => { const v = Number(e.target.value); setDelay(v); saveSettings({ delay_minutes: v }); }} data-testid="feedback-delay">
              <option value={0}>Immediately</option>
              <option value={120}>+2 hours</option>
              <option value={1440}>+24 hours</option>
            </select>
          </label>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Automation is currently disabled globally. Use the "Send now" button above to trigger sends manually.
        </div>
      </div>

      {/* Responses */}
      <div className="glass rounded-2xl p-6">
        <Overline>Responses ({data.responses.length})</Overline>
        {data.responses.length === 0 ? (
          <div className="mt-3 text-sm text-muted-foreground">No feedback submitted yet.</div>
        ) : (
          <div className="mt-3 space-y-3">
            {data.responses.map((r) => (
              <div key={r.id} className="p-4 rounded-xl border border-border" data-testid={`feedback-response-${r.id}`}>
                <div className="flex items-center gap-3">
                  <span className="chip font-mono" style={{ background: `${npsColour(r.nps_bucket)}20`, color: npsColour(r.nps_bucket) }}>NPS {r.nps}</span>
                  <span className="text-sm">{r.name || r.email}</span>
                  <span className="text-xs text-muted-foreground font-mono ml-auto">{new Date(r.submitted_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                </div>
                {r.q1_worked && <div className="mt-2 text-sm"><span className="text-muted-foreground text-xs uppercase tracking-wider">Worked well · </span>{r.q1_worked}</div>}
                {r.q2_improve && <div className="mt-1 text-sm"><span className="text-muted-foreground text-xs uppercase tracking-wider">Could be better · </span>{r.q2_improve}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- INSIGHTS ---------------- */
function InsightsTab({ ev }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/events/${ev.id}/insights`).then((r) => setData(r.data)); }, [ev.id]);
  if (!data) return <div className="skeleton h-64" />;
  const max = Math.max(1, ...data.registrations_by_day.map((r) => r.count));
  return (
    <div className="space-y-6" data-testid="insights-tab">
      <div className="grid md:grid-cols-4 gap-4">
        <Stat label="Registrations" value={data.total_registrations} testId="insight-regs" />
        <Stat label="Going" value={data.going} testId="insight-going" />
        <Stat label="Attendance" value={`${data.attendance_rate}%`} testId="insight-attend" />
        <Stat label="Avg. rating" value={data.avg_rating ?? "—"} testId="insight-rating" />
      </div>
      <div className="card-hair p-6">
        <Overline>Registrations over time</Overline>
        <div className="mt-6 flex items-end gap-2 h-40">
          {data.registrations_by_day.length === 0 ? (
            <div className="text-sm text-muted-foreground">No data yet.</div>
          ) : data.registrations_by_day.map((r) => (
            <div key={r.date} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t-lg" style={{ background: "hsl(var(--vd-blue))", height: `${(r.count / max) * 100}%`, minHeight: "4px" }} />
              <div className="text-[9px] text-muted-foreground font-mono">{r.date.slice(5)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="card-hair p-6" data-testid={testId}>
      <div className="overline text-muted-foreground">{label}</div>
      <div className="stat-value text-4xl mt-2">{value}</div>
    </div>
  );
}

/* ---------------- MORE ---------------- */
function MoreTab({ ev, onChange, nav }) {
  const duplicate = async () => {
    try {
      const r = await api.post(`/events/${ev.id}/duplicate`);
      toast.success("Duplicated");
      nav(`/admin/events/${r.data.id}/manage`);
    } catch { toast.error("Failed"); }
  };
  const cancelEv = async () => {
    if (!window.confirm("Cancel this event? Guests will be notified.")) return;
    try {
      await api.post(`/events/${ev.id}/cancel`);
      toast.success("Event cancelled");
      onChange();
    } catch { toast.error("Failed"); }
  };
  const deleteEv = async () => {
    if (!window.confirm("Delete this event permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success("Deleted");
      nav("/admin/events");
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3 max-w-xl" data-testid="more-tab">
      <button onClick={duplicate} className="card-hair p-4 w-full text-left hover-lift flex items-center gap-3" data-testid="duplicate-btn">
        <MoreHorizontal size={16} />
        <div>
          <div className="font-medium">Duplicate event</div>
          <div className="text-xs text-muted-foreground">Create an editable copy with the same setup.</div>
        </div>
      </button>
      <button onClick={cancelEv} className="card-hair p-4 w-full text-left hover-lift flex items-center gap-3" data-testid="cancel-event-btn">
        <X size={16} style={{ color: "hsl(var(--vd-orange))" }} />
        <div>
          <div className="font-medium">Cancel event</div>
          <div className="text-xs text-muted-foreground">Marks the event cancelled and notifies guests.</div>
        </div>
      </button>
      <button onClick={deleteEv} className="card-hair p-4 w-full text-left hover-lift flex items-center gap-3" data-testid="delete-event-btn">
        <Trash2 size={16} style={{ color: "hsl(var(--vd-coral))" }} />
        <div>
          <div className="font-medium" style={{ color: "hsl(var(--vd-coral))" }}>Delete event</div>
          <div className="text-xs text-muted-foreground">Permanent — removes all registrations and broadcasts.</div>
        </div>
      </button>
    </div>
  );
}
